import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';

const STATUS_CONFIG = {
  overdue:  { label: 'Overdue',    color: 'red',   cardClass: 'maint-status__stat-card--red' },
  due_now:  { label: 'Due Now',    color: 'blue',  cardClass: 'maint-status__stat-card--blue' },
  due_soon: { label: 'Due Soon',   color: 'amber', cardClass: 'maint-status__stat-card--amber' },
  not_due:  { label: 'Up to Date', color: 'green', cardClass: 'maint-status__stat-card--green' },
};

function formatMileage(value) {
  if (!value && value !== 0) return '\u2014';
  return `${Number(value).toLocaleString()} mi`;
}

function formatDate(dateStr) {
  if (!dateStr) return '\u2014';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function TruckMaintenancePage() {
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const [entries, setEntries] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [maintTypes, setMaintTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [filterTruck, setFilterTruck] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Summary counts
  const [summary, setSummary] = useState({ overdue: 0, due_now: 0, due_soon: 0, not_due: 0 });

  const loadData = useCallback(async () => {
    try {
      const filters = {};
      if (filterTruck) filters.truck_id = filterTruck;
      if (filterType) filters.maintenance_type_id = filterType;
      if (filterStatus) filters.status = filterStatus;

      const data = await api.getTruckMaintenanceStatus(filters);
      const rows = Array.isArray(data) ? data : (data.entries || []);
      setEntries(rows);

      // Compute summary from full dataset (unfiltered counts ideally come from server,
      // but we can derive from current result set)
      const counts = { overdue: 0, due_now: 0, due_soon: 0, not_due: 0 };
      rows.forEach(r => {
        if (counts.hasOwnProperty(r.status)) counts[r.status]++;
      });
      setSummary(counts);
      setError(null);
    } catch (err) {
      console.error('Failed to load maintenance status:', err);
      setError('Failed to load maintenance status');
    }
    setLoading(false);
  }, [filterTruck, filterType, filterStatus]);

  // Load filter options once
  useEffect(() => {
    api.getTrucks().then(setTrucks).catch(console.error);
    api.getMaintenanceTypes().then(data => {
      setMaintTypes(Array.isArray(data) ? data : (data.types || []));
    }).catch(console.error);
  }, []);

  // Load data + polling
  useEffect(() => {
    setLoading(true);
    loadData();

    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  function handleSearch() {
    setLoading(true);
    loadData();
  }

  return (
    <div className="maint-status">
      <div className="maint-status__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h1>Truck Maintenance Status</h1>
        <button type="button" className="btn btn-outline" onClick={() => navigate('/maintenance/preventive')}>
          ← Bucket
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Summary Cards */}
      <div className="kpi-grid">
        <div className="kpi-card" style={{ '--kpi-accent': '#ef4444' }}>
          <div className="kpi-value">{summary.overdue}</div>
          <div className="kpi-label">Overdue</div>
        </div>
        <div className="kpi-card" style={{ '--kpi-accent': '#f59e0b' }}>
          <div className="kpi-value">{summary.due_now}</div>
          <div className="kpi-label">Due Now</div>
        </div>
        <div className="kpi-card" style={{ '--kpi-accent': '#3b82f6' }}>
          <div className="kpi-value">{summary.due_soon}</div>
          <div className="kpi-label">Due Soon</div>
        </div>
        <div className="kpi-card" style={{ '--kpi-accent': '#10b981' }}>
          <div className="kpi-value">{summary.not_due}</div>
          <div className="kpi-label">Up to Date</div>
        </div>
      </div>

      {/* Filters */}
      <div className="maint-status__filters">
        <div className="form-group">
          <label>Truck</label>
          <select className="form-input" value={filterTruck} onChange={e => setFilterTruck(e.target.value)}>
            <option value="">All Trucks</option>
            {trucks.map(t => (
              <option key={t.id} value={t.id}>{t.unit_number || t.name || `Truck #${t.id}`}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Maintenance Type</label>
          <select className="form-input" value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">All Types</option>
            {maintTypes.map(mt => (
              <option key={mt.id} value={mt.id}>{mt.name}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Status</label>
          <select className="form-input" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="overdue">Overdue</option>
            <option value="due_now">Due Now</option>
            <option value="due_soon">Due Soon</option>
            <option value="not_due">Up to Date</option>
          </select>
        </div>
        <div className="form-group maint-status__filter-action">
          <button className="btn btn-gold" onClick={handleSearch}>Search</button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading-spinner">Loading maintenance status...</div>
      ) : (
        <div className="table-responsive">
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Truck</th>
                <th>Maintenance Type</th>
                <th>Last Done (Mileage)</th>
                <th>Last Done (Date)</th>
                <th>Next Due (Mileage)</th>
                <th>Next Due (Date)</th>
                <th>Status</th>
                <th>Work Order</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr><td colSpan="8" className="text-center">No maintenance records found.</td></tr>
              ) : entries.map((entry, idx) => {
                const cfg = STATUS_CONFIG[entry.status] || STATUS_CONFIG.not_due;
                const isOverdue = entry.status === 'overdue';
                return (
                  <tr key={entry.id || `${entry.truck_id}-${entry.maintenance_type_id}-${idx}`}
                      className={isOverdue ? 'maint-status__row--overdue' : ''}>
                    <td><strong>{entry.truck_name || entry.unit_number || `Truck #${entry.truck_id}`}</strong></td>
                    <td>{entry.type_name || entry.maintenance_type_name || '\u2014'}</td>
                    <td>{formatMileage(entry.last_done_mileage)}</td>
                    <td>{formatDate(entry.last_done_date)}</td>
                    <td>{formatMileage(entry.next_due_mileage)}</td>
                    <td>{formatDate(entry.next_due_date)}</td>
                    <td>
                      <span className={`status-badge status-badge--${cfg.color}`}>
                        {cfg.label}
                      </span>
                    </td>
                    <td>
                      {entry.work_order_id ? (
                        <a href={`/maintenance/work-orders`} className="maint-status__wo-link">
                          WO #{entry.work_order_id}
                        </a>
                      ) : '\u2014'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
